import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';
import { featuredComics, latestUpdates, popularRanking, categories } from '@/mocks/home';
import { articleChapters } from '@/mocks/articles';
import type { ComicItem } from '@/mocks/home';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => {
    const all = [...featuredComics, ...latestUpdates, ...popularRanking];
    const seen = new Set<string>();
    const unique: ComicItem[] = [];
    for (const c of all) {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        unique.push(c);
      }
    }
    return unique.find((c) => c.id === id) || null;
  }, [id]);

  const chapters = useMemo(() => {
    if (!id) return [];
    return articleChapters[id] || [];
  }, [id]);

  const categoryInfo = useMemo(() => {
    if (!comic) return null;
    return categories.find((c) => c.slug === comic.category) || null;
  }, [comic]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    const all = [...featuredComics, ...latestUpdates, ...popularRanking];
    const seen = new Set([comic.id]);
    const related: ComicItem[] = [];
    for (const c of all) {
      if (!seen.has(c.id) && c.category === comic.category) {
        seen.add(c.id);
        related.push(c);
      }
      if (related.length >= 4) break;
    }
    return related;
  }, [comic]);

  const articleSchema = useMemo(() => {
    if (!comic) return {};
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 韩漫在线观看`,
      description: comic.description,
      author: {
        '@type': 'Person',
        name: comic.author,
      },
      datePublished: comic.updateTime,
      dateModified: comic.updateTime,
      image: comic.coverImage,
      publisher: {
        '@type': 'Organization',
        name: '韩漫网',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://thingschat.cn/article/${comic.id}`,
      },
    };
  }, [comic]);

  const breadcrumbSchema = useMemo(() => {
    if (!comic) return [];
    return [
      { label: '首页', to: '/' },
      { label: `${categoryInfo?.name || '漫画'}`, to: `/category/${comic.category}` },
      { label: comic.title },
    ];
  }, [comic, categoryInfo]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="mt-6 text-xl font-bold text-foreground-900">漫画未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Article" data={articleSchema} />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbSchema} />

          <div className="mt-6 flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-72 flex-shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 韩漫在线观看`}
                  title={`${comic.title} - 好看的韩国漫画全集`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                {comic.title}
              </h1>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-500">
                  状态：
                  <span className={comic.status === '连载中' ? 'text-accent-600 font-medium' : 'text-foreground-700 font-medium'}>
                    {comic.status}
                  </span>
                </span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-500">共 {comic.totalChapters} 话</span>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`text-sm w-4 h-4 flex items-center justify-center ${
                        star <= Math.round(comic.rating / 2)
                          ? 'ri-star-fill text-primary-500'
                          : 'ri-star-line text-foreground-300'
                      }`}
                    ></i>
                  ))}
                  <span className="text-base font-bold text-primary-600 ml-1">{comic.rating}</span>
                </div>
                <span className="text-sm text-foreground-400">
                  {(comic.views / 10000).toFixed(0)}万阅读
                </span>
              </div>

              <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                {comic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${comic.category}`}
                    className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-5 p-4 rounded-xl bg-background-100">
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                  从第一话开始阅读
                </button>
                <button className="flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line w-4 h-4 flex items-center justify-center"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">章节列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {chapters.map((chapter) => (
                <a
                  key={chapter.id}
                  href="#"
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-100/50 transition-colors group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-700 font-medium line-clamp-1">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{chapter.updateTime}</span>
                </a>
              ))}
              {chapters.length === 0 && (
                <div className="col-span-full text-center py-8">
                  <p className="text-sm text-foreground-500">暂无章节</p>
                </div>
              )}
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/article/${rc.id}`}
                    className="group block"
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        title={`${rc.title} - 韩漫在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="mt-2.5 text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}